import {
  createContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { isEqual } from 'lodash';

function getInitialState({ members, accessConfig }) {
  accessConfig = accessConfig ?? { all: true };

  if (!members) {
    return {
      selectedMembers: {},
    };
  }

  const selectedMembers = Object.fromEntries(
    members.map(({ user_group_member_id, cohort }) => [
      user_group_member_id,
      (accessConfig?.all ||
        accessConfig?.members?.[user_group_member_id] ||
        accessConfig?.cohorts?.[cohort]) ??
        false,
    ])
  );

  return {
    selectedMembers,
  };
}

function memberReducer(state, action) {
  switch (action.type) {
    case 'reset':
      return getInitialState({
        members: action.members,
        accessConfig: action.accessConfig,
      });
    case 'select':
      if (
        state.selectedMembers[action.memberId] !== (action.selected ?? true)
      ) {
        return {
          ...state,
          selectedMembers: {
            ...state.selectedMembers,
            [action.memberId]: action.selected ?? true,
          },
        };
      }

      return state;

    case 'selectAll':
      const selected = action.selected ?? true;
      const newState = state.selectedMembers;
      const modifiedState = action.memberIds.reduce((acc, memberId) => {
        if (state.selectedMembers[memberId] !== selected) {
          newState[memberId] = selected;
          return true;
        }
        return acc;
      }, false);

      return modifiedState
        ? {
            ...state,
            selectedMembers: {
              ...state.selectedMembers,
            },
          }
        : state;

    default:
      return state;
  }
}

export const SelectedMemberContext = createContext({
  uniqueContextId: Symbol('member-selection'),
  select: (_memberId, _selected) => {},
  selectAll: (_memberIds, _selected) => {},
  isSelected: (_memberId) => true,
  selectedMembers: {},
});

export function SelectedMemberProvider({
  userGroupId,
  members,
  accessConfig,
  children,
  onSelectedMembersChange = () => {},
}) {
  const [state, dispatch] = useReducer(
    memberReducer,
    { members, accessConfig },
    getInitialState
  );
  const initialized = useRef(false);

  const select = useCallback((memberId, selected = true) => {
    dispatch({ type: 'select', memberId, selected });
  }, []);
  const selectAll = useCallback((memberIds, selected = true) => {
    dispatch({ type: 'selectAll', memberIds, selected });
  }, []);
  const isSelected = useCallback(
    (memberId) => {
      return state.selectedMembers[memberId] ?? true;
    },
    [state.selectedMembers]
  );

  useEffect(() => {
    if (!initialized.current && accessConfig !== undefined && members) {
      dispatch({ type: 'reset', members, accessConfig });
      initialized.current = true;
    }
  }, [accessConfig, members]);

  // if the user group changes, reset the selected members
  useEffect(() => {
    initialized.current = undefined;
  }, [userGroupId]);

  useEffect(() => {
    const selectedMemberData = (members ?? []).reduce(
      (acc, { user_group_member_id, cohort }) => {
        const cohortAcc = (acc.cohorts[cohort] = acc.cohorts[cohort] ?? {
          all: true,
          members: {},
        });

        if (state.selectedMembers[user_group_member_id]) {
          cohortAcc.members[user_group_member_id] = true;
        } else {
          cohortAcc.all = false;
        }

        acc.all = acc.all && state.selectedMembers[user_group_member_id];

        return acc;
      },
      { cohorts: {}, all: true }
    );

    const newAccessConfig = selectedMemberData.all
      ? { all: true }
      : Object.entries(selectedMemberData.cohorts).reduce(
          (acc, [cohort, { all, members }]) => {
            return all
              ? {
                  ...acc,
                  cohorts: {
                    ...acc.cohorts,
                    [cohort]: true,
                  },
                }
              : {
                  ...acc,
                  members: {
                    ...acc.members,
                    ...members,
                  },
                };
          },
          { cohorts: {}, members: {} }
        );

    if (initialized.current && !isEqual(newAccessConfig, accessConfig)) {
      onSelectedMembersChange(newAccessConfig);
    }
  }, [state.selectedMembers, members, onSelectedMembersChange, accessConfig]);

  const context = useMemo(
    () => ({
      select,
      selectAll,
      isSelected,
      uniqueContextId: Symbol(`member-selection-${userGroupId}`),
      selectedMembers: state.selectedMembers,
    }),
    [userGroupId, state.selectedMembers, select, selectAll, isSelected]
  );

  return (
    <SelectedMemberContext.Provider value={context}>
      {children}
    </SelectedMemberContext.Provider>
  );
}

export function useSelectedMemberContext() {
  const context = useContext(SelectedMemberContext);
  if (!context) {
    throw new Error(
      'useSelectedMemberContext must be used within a SelectedMemberProvider'
    );
  }
  return context;
}
